/**
 * API client for the Perfex import (`crm-import/perfex`, mounted under
 * `/api/admin/crm/import`).
 *
 * Its own file beside `browserRendering.ts` and for the same reason: one screen
 * talks to these six routes and nothing else in the CRM ever will. The screen
 * imports the functions below and never builds a URL of its own, so the day a
 * route moves there is exactly one file to change.
 *
 * ENTITY AND MODEL KEYS STAY PLAIN STRINGS on purpose. The server owns
 * `IMPORT_ORDER` and `IMPORT_MODELS`; a second, narrower copy over here would
 * typecheck green while the table silently dropped a row for an entity the
 * server had learned and the CRM had not. Everything on screen is drawn from
 * what the response actually contains, and the label lookups fall back to the
 * raw key rather than to nothing.
 */
import { api } from "./api";

/** Where the legacy database lives. Deliberately no user and no password: the
 *  server strips them out of the connection string before this leaves it. */
export interface ImportTarget {
  host: string;
  database: string;
}

/** The result of actually opening a connection, not of parsing the setting. */
export type ImportConnection =
  | { ok: true; version: string; database: string }
  | { ok: false; error: string };

/** One entity's tally inside a run. */
export interface ImportEntityStat {
  entity: string;
  /** Rows read from the legacy side. */
  read: number;
  /** Rows that did not exist here and were inserted. */
  created: number;
  /** Rows that already existed under the same legacy id and were overwritten. */
  updated: number;
  /** Rows deliberately passed over, almost always an unresolvable foreign key. */
  skipped: number;
  /** Wall time for this entity, so a slow table is visible without a profiler. */
  ms: number;
}

/**
 * A run's lifecycle: running | done | failed | reverting | reverted.
 *
 * A union for the phrasing lookup, but `ImportRun.status` stays a plain string
 * for the reason given in this file's header - a status added on the server
 * must draw as itself rather than as a blank pill.
 */
export type ImportRunStatus = "running" | "done" | "failed" | "reverting" | "reverted";

export interface ImportRun {
  id: string;
  status: string;
  /** A dry run resolves everything and counts what it would write, then writes
   *  nothing at all - not even a ledger row. */
  dryRun: boolean;
  startedAt: string;
  finishedAt: string | null;
  /** The failure that stopped the run, in plain text. Null on success. */
  error: string | null;
  /** Set once this run's rows were deleted again. */
  revertedAt: string | null;
  entities: string[];
  entityStats: ImportEntityStat[];
}

/** A row that did not make it, and why. Without these a dropped row is silent. */
export interface ImportIssue {
  id: string;
  entity: string;
  legacyId: string | null;
  reason: string;
  detail: string | null;
  createdAt: string;
}

/**
 * One run with everything it recorded.
 *
 * Flat on purpose: the server answers `{ run, issues, issueCount, issueLimit }`
 * and the unwrapping happens in `getImportRun` below, so no card has to know
 * that the report arrives in two halves.
 */
export interface ImportRunDetail extends ImportRun {
  issues: ImportIssue[];
  /** How many issues the run really has. `issues` is capped at `issueLimit`. */
  issueCount: number;
  issueLimit: number;
}

/** How many rows the ledger says the import owns, per Prisma model. */
export interface ImportFootprint {
  model: string;
  rows: number;
}

export interface ImportStatus {
  /** Whether a connection string exists at all. */
  configured: boolean;
  target: ImportTarget | null;
  /** Null while nothing is configured - there is no connection to have tried. */
  connection: ImportConnection | null;
  /** The run in progress, if any. The screen polls this one. */
  running: ImportRun | null;
  footprint: ImportFootprint[];
}

export interface EntityCoverage {
  entity: string;
  /** The resolved legacy table, or null when this installation has none. */
  table: string | null;
  sourceRows: number;
  /** Rows here that came from the legacy system. */
  importedRows: number;
  /** Rows here that did NOT. The undo never touches these. */
  ownRows: number;
}

export interface SkippedTable {
  table: string;
  rows: number;
  /** register | noise | archive | decayed | manual | unused | ephemeral | lookup | uncovered */
  reason: string;
}

export interface Introspection {
  database: string;
  version: string;
  tableCount: number;
  entities: EntityCoverage[];
  /** Non-empty legacy tables no entity covers, largest first. */
  skipped: SkippedTable[];
}

/** A row the undo left alone, and what is holding it. */
export interface RevertKeptExample {
  recordId: string;
  heldBy: string;
}

export interface RevertModelResult {
  model: string;
  /** Rows the ledger says this undo owns. */
  owned: number;
  deleted: number;
  /** Rows left in place because something newer points at them. */
  kept: number;
  keptExamples: RevertKeptExample[];
}

export interface RevertResult {
  runIds: string[];
  dryRun: boolean;
  models: RevertModelResult[];
  totalDeleted: number;
  totalKept: number;
}

export const getImportStatus = (): Promise<ImportStatus> => api.get<ImportStatus>("/admin/crm/import/status");

/** Reads the legacy schema. Costs a connection and a count per table, so the
 *  screen asks for it once per load rather than on every poll. */
export const introspectImport = async (): Promise<Introspection> => {
  const res = await api.get<{ introspection: Introspection }>("/admin/crm/import/introspect");
  return res.introspection;
};

/**
 * Starts a run and returns immediately with its id - the work carries on
 * server-side, so a closed tab does not stop an import halfway.
 *
 * `entities` omitted means every entity. The screen always sends the list it
 * has ticked, because "all" read off a stale introspection is not the same set
 * as "all" on the server.
 */
export const startImport = (body: {
  entities?: string[];
  dryRun?: boolean;
  limit?: number;
}): Promise<{ runId: string }> => api.post<{ runId: string }>("/admin/crm/import/run", body);

export const listImportRuns = async (): Promise<ImportRun[]> => {
  const res = await api.get<{ runs: ImportRun[] }>("/admin/crm/import/runs");
  return res.runs;
};

/**
 * One run's report, flattened.
 *
 * The two halves are joined here rather than in the cards because the split is
 * a server-side paging decision (the issue list is capped, the run row is not)
 * and nothing on screen treats them as separate things. Flattening in one place
 * also keeps `isRunActive` honest: it reads `status`, and a caller handed the
 * raw envelope would be asking an object that has no `status` at all, get
 * `undefined`, and conclude that a running import had already finished.
 */
export const getImportRun = async (id: string, signal?: AbortSignal): Promise<ImportRunDetail> => {
  const res = await api.get<{
    run: ImportRun;
    issues: ImportIssue[];
    issueCount: number;
    issueLimit: number;
  }>(`/admin/crm/import/runs/${encodeURIComponent(id)}`, signal);
  return { ...res.run, issues: res.issues, issueCount: res.issueCount, issueLimit: res.issueLimit };
};

/**
 * Plans - and unless `dryRun` performs - the undo.
 *
 * `runIds` omitted means every revertable run. The interesting half of the
 * answer is `totalKept`: a kept row is one our own newer data points at, which
 * is the importer refusing to take something with it.
 */
/**
 * How a Perfex staff row currently resolves to a platform user. `link` is a
 * human's decision, `email` and `name` are matches, `excluded` is a decision to
 * leave the row unowned, and `unresolved` is nobody having decided and nothing
 * having matched.
 */
export type StaffVia = "link" | "email" | "name" | "excluded" | "unresolved";

export interface StaffWorkload {
  projects: number;
  tasks: number;
  timeEntries: number;
  tickets: number;
}

export interface StaffReviewRow {
  staffId: string;
  name: string | null;
  email: string | null;
  active: boolean;
  via: StaffVia;
  userId: string | null;
  userLabel: string | null;
  workload: StaffWorkload;
  rowsOwned: number;
}

export interface PlatformUserOption {
  id: string;
  email: string;
  name: string;
}

export interface StaffReview {
  staff: StaffReviewRow[];
  users: PlatformUserOption[];
  unresolvedOwningWork: number;
}

export const getImportStaff = (): Promise<StaffReview> =>
  api.get<StaffReview>("/admin/crm/import/staff");

/**
 * The whole map, never a patch: an absent key means "nobody has decided" and a
 * `null` means "decided: nobody", and only sending everything can tell them
 * apart. See the route's own note.
 */
export const putImportStaff = (
  links: Record<string, string | null>,
): Promise<PutStaffResult> => api.put<PutStaffResult>("/admin/crm/import/staff", { links });

export interface PutStaffResult {
  links: Record<string, string | null>;
  /**
   * The refreshed screen, or null when the old CRM could not be reached to draw
   * it. Null does NOT mean the save failed - the decision is stored either way.
   * Only the preview needs the source; see the route's own note.
   */
  review: StaffReview | null;
  /** Set when the save succeeded but the preview could not be rebuilt. */
  reviewUnavailable?: boolean;
}

export const revertImport = (body: { runIds?: string[]; dryRun?: boolean }): Promise<RevertResult> =>
  api.post<RevertResult>("/admin/crm/import/revert", body);

/** Whether a run is still working. One place, because the poll and the buttons
 *  must never disagree about it. */
export function isRunActive(run: { status: string } | null | undefined): boolean {
  return run?.status === "running" || run?.status === "reverting";
}
